import React, {
  Dispatch,
  FocusEvent,
  JSX,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react';
import { Collapse, Input, InputProps } from 'reactstrap';
import { InputType } from 'reactstrap/types/lib/Input';
import { ControllerRenderProps, FieldValues, UseFormSetValue } from 'react-hook-form';
import { BaseFormElement, InputSearchDropdownOption } from '../types/types';
import { useClickAway } from 'react-use';

export interface FormInputSearchDropdownProps {
  tag: BaseFormElement['tag'];
  name: string;
  baseName?: string;
  label?: string;
  boldLabel?: boolean;
  className?: string;
  errorsProp?: Record<string, string>;
  type?: InputType;
  autoComplete?: string;
  showErrors?: boolean;
  showErrorIcon?: boolean;
  disablePaste?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  options: Array<InputSearchDropdownOption>;
}

const FormInputSearchDropdownCore = (
  props: FormInputSearchDropdownProps & {
    field: ControllerRenderProps<FieldValues, string>;
    collapseOpen: boolean;
    setCollapseOpen: Dispatch<SetStateAction<boolean>>;
    exposeSeparateFieldValue?: (fieldName: string, value: Record<string, unknown>) => void;
    setValue?: UseFormSetValue<FieldValues>;
  } & Omit<InputProps, 'tag'>
): JSX.Element | null => {
  const {
    tag,
    name,
    className,
    type,
    autoComplete,
    disabled,
    placeholder,
    disablePaste,
    onFocus,
    onBlur,
    options,
    field,
    collapseOpen,
    setCollapseOpen,
    exposeSeparateFieldValue,
    setValue
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
  const [selectedOptionCode, setSelectedOptionCode] = useState<string | null>(null);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  optionRefs.current = [];

  useClickAway(containerRef, () => {
    setCollapseOpen(false);
  });

  useEffect(() => {
    if (!collapseOpen) setFocusedOptionIndex(-1);
  }, [collapseOpen]);

  useEffect(() => {
    if (focusedOptionIndex >= 0) {
      optionRefs.current[focusedOptionIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [focusedOptionIndex]);

  const onOptionClick = (
    code: string,
    fullObject?: Record<string, unknown>,
    displayValue?: string
  ): void => {
    if (exposeSeparateFieldValue && fullObject) {
      exposeSeparateFieldValue(name, fullObject);
    }
    if (setValue) {
      setValue(name, displayValue ?? code);
    }
    setSelectedOptionCode(code);
    setCollapseOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    const count = options.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!collapseOpen) {
          setCollapseOpen(true);
          setFocusedOptionIndex(0);
        } else {
          setFocusedOptionIndex(prev => (prev + 1) % count);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!collapseOpen) {
          setCollapseOpen(true);
          setFocusedOptionIndex(count - 1);
        } else {
          setFocusedOptionIndex(prev => (prev <= 0 ? count - 1 : prev - 1));
        }
        break;
      case 'Enter':
        if (collapseOpen && focusedOptionIndex >= 0) {
          e.preventDefault();
          const opt = options[focusedOptionIndex];
          if (opt) onOptionClick(opt.code, opt.fullObject, opt.displayValue);
        }
        break;
      case 'Escape':
        if (collapseOpen) {
          e.preventDefault();
          setCollapseOpen(false);
        }
        break;
      case 'Tab':
        setCollapseOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="position-relative">
      <Input
        id={`form-builder-input-${name}-${tag}`}
        /* eslint-disable-next-line */
        {...field}
        data-testid={'form-input-' + name}
        value={field.value}
        type={type || 'text'}
        onFocus={event => {
          if (onFocus) {
            onFocus(event);
          }
          if (!collapseOpen) {
            setCollapseOpen(true);
          }
        }}
        onBlur={e => {
          field.onBlur();
          if (onBlur) {
            onBlur(e);
          }
        }}
        onChange={e => {
          setSelectedOptionCode(null);
          field.onChange(e.target.value);
        }}
        onClick={(): void => setCollapseOpen(true)}
        onKeyDown={onKeyDown}
        className={`${className ?? ''} ${field.value ? 'input--filled' : ''}`}
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder={placeholder}
        onPaste={(e): void => {
          if (disablePaste) {
            e.preventDefault();
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={collapseOpen}
      />
      <Collapse
        className="form-input__collapse"
        isOpen={!!options && options.length > 0 && collapseOpen}
        role="listbox"
      >
        {options.map(({ code, label: optionLabel, fullObject, indexer, displayValue }, index) => (
          <div
            key={indexer || code || JSON.stringify(fullObject)}
            role="option"
            aria-selected={selectedOptionCode === code}
            tabIndex={-1}
            ref={(el): void => {
              optionRefs.current[index] = el;
            }}
            className={`form-input__collapse__option ${
              focusedOptionIndex === index ? 'form-input__collapse__option--focused' : ''
            } ${selectedOptionCode === code ? 'form-input__collapse__option--selected' : ''}
            `}
            onClick={(): void => onOptionClick(code, fullObject, displayValue)}
            onKeyDown={(e): void => {
              if (e.key === 'Enter' || e.key === ' ') onOptionClick(code, fullObject, displayValue);
            }}
          >
            {optionLabel}
          </div>
        ))}
      </Collapse>
    </div>
  );
};

export default FormInputSearchDropdownCore;
